import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { SavingsLite } from '../modules/Savings/types/response/SavingResponseLite';
import { SavingsSummary } from '../modules/Savings/types/response/Summary';

type PageIndex = number;

export interface CursorPage<T> {
	items: T[];
}

export interface SavingsState {
	page: number;
	rowsPerPage: number;

	// cursor paging
	cursors: Array<number | null>; // cursors[0] = null (first page)
	pageCache: Record<PageIndex, CursorPage<SavingsLite>>;
	nextCursorByPage: Record<PageIndex, number | null>;

	virtualCount: number; // UX count for pagination
	summary?: SavingsSummary | null;
}

const initialState: SavingsState = {
	page: 0,
	rowsPerPage: 50,
	cursors: [null],
	pageCache: {},
	nextCursorByPage: {},
	virtualCount: 0,
	summary: null,
};

const savingsSlice = createSlice({
	name: 'savings',
	initialState,
	reducers: {
		resetSavingsState: () => initialState,

		// when filters or rpp change, clear cursor paging
		resetSavingsCursorPaging(state) {
			state.page = 0;
			state.cursors = [null];
			state.pageCache = {};
			state.nextCursorByPage = {};
			state.virtualCount = 0;
		},

		setSavingsRowsPerPage(state, action: PayloadAction<number>) {
			state.rowsPerPage = action.payload;
		},

		setSavingsPage(state, action: PayloadAction<number>) {
			state.page = action.payload;
		},

		setSavingsPageData(
			state,
			action: PayloadAction<{ page: number; items: SavingsLite[]; nextCursor?: number | null }>,
		) {
			const { page, items, nextCursor } = action.payload;
			state.pageCache[page] = { items };
			state.nextCursorByPage[page] = nextCursor ?? null;
			state.cursors[page + 1] = nextCursor ?? null;

			const maxLoaded = Math.max(0, ...Object.keys(state.pageCache).map(n => +n));
			const hasNext = state.nextCursorByPage[maxLoaded] != null;
			state.virtualCount = (maxLoaded + 1) * state.rowsPerPage + (hasNext ? 1 : 0);
		},

		setSavingsSummary(state, action: PayloadAction<SavingsSummary | null | undefined>) {
			state.summary = action.payload ?? null;
		},
	},
});

export const {
	resetSavingsState,
	resetSavingsCursorPaging,
	setSavingsRowsPerPage,
	setSavingsPage,
	setSavingsPageData,
	setSavingsSummary,
} = savingsSlice.actions;

export default savingsSlice.reducer;

// selectors
export const selectSavingsPage = (s: RootState) => s.saving.page;
export const selectSavingsRowsPerPage = (s: RootState) => s.saving.rowsPerPage;
export const selectSavingsSummary = (s: RootState) => s.saving.summary;

export const selectSavingsItems = (s: RootState) => {
	const p = s.saving.page;
	return s.saving.pageCache[p]?.items ?? [];
};
export const selectSavingsCount = (s: RootState) => s.saving.virtualCount;

export const selectSavingsCursorForPage = (p: number) => (s: RootState) =>
	s.saving.cursors[p] ?? null;

export const selectSavingsHasCachedPage = (p: number) => (s: RootState) =>
	Boolean(s.saving.pageCache[p]);
